import { useEffect, useMemo, useState } from 'react';

import AppModal from '../components/AppModal';
import api from '../services/api';
import { esAdministrador } from '../utils/usuarioActual';
import {
  activarConceptoCajaChica,
  activarFondoCajaChica,
  activarResponsableCajaChica,
  actualizarResponsableCajaChica,
  asignarResponsableFondoCajaChica,
  cancelarGastoCajaChica,
  crearConceptoCajaChica,
  crearFondoCajaChica,
  descargarEvidenciaGasto,
  desactivarConceptoCajaChica,
  desactivarFondoCajaChica,
  desactivarResponsableCajaChica,
  editarConceptoCajaChica,
  editarFondoCajaChica,
  listarArqueosCajaChica,
  listarFondosCajaChica,
  listarConceptosCajaChica,
  listarEvidenciasGasto,
  listarGastosCajaChica,
  listarResponsablesFondoCajaChica,
  listarUsuariosDisponiblesCajaChica,
  obtenerMisCajasChicas,
  obtenerResumenCajaChica,
  registrarArqueoCajaChica,
  registrarFondeoCajaChica,
  registrarReposicionCajaChica,
  registrarGastoCajaChica,
  subirEvidenciaGasto
} from '../services/cajaChicaService';

const FORM_GASTO_INICIAL = {
  monto: '',
  conceptoTexto: '',
  conceptoId: '',
  descripcion: '',
  referencia: '',
  archivo: null
};

const FORM_ARQUEO_INICIAL = {
  saldoFisico: '',
  observaciones: ''
};
const FORM_FONDO_INICIAL = {
  Id: null,
  Nombre: '',
  Descripcion: '',
  MonedaId: '',
  MontoFondoAutorizado: '',
  Activa: true
};

const FORM_CONCEPTO_INICIAL = {
  Id: null,
  Nombre: '',
  Descripcion: '',
  Activo: true
};

const FORM_ENTRADA_INICIAL = {
  tipo: 'FONDEO',
  monto: '',
  concepto: '',
  referencia: '',
  folioRecibo: '',
  reciboId: ''
};

const FORM_RESPONSABLE_INICIAL = {
  UsuarioId: '',
  PuedeRegistrarGastos: true,
  PuedeSubirEvidencia: true,
  PuedeVerSaldo: true
};

function formatoImporte(valor, moneda = 'MXN') {
  const texto = String(moneda || '').toUpperCase();
  const currency = texto.includes('USD') || texto.includes('DOLAR') || texto.includes('DÓLAR') ? 'USD' : 'MXN';

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(Number(valor || 0));
}

function formatoFecha(valor) {
  return String(valor || '').replace('T', ' ').slice(0, 19);
}

function mensajeError(error) {
  return error.response?.data?.detalle || error.response?.data?.mensaje || error.message || 'No fue posible procesar la solicitud.';
}

function obtenerClaseEstatusCajaChica(estatus) {
  const valor = String(estatus || '').toUpperCase();

  if (valor === 'REGISTRADO') return 'status-badge status-badge-registrado';
  if (valor === 'CANCELADO') return 'status-badge status-badge-cancelado';
  if (valor === 'REVISADO') return 'status-badge status-badge-revisado';
  if (valor === 'RECHAZADO') return 'status-badge status-badge-rechazado';
  if (valor === 'PENDIENTE') return 'status-badge status-badge-pendiente';
  if (valor === 'ACTIVO') return 'status-badge status-badge-activo';
  if (valor === 'INACTIVO') return 'status-badge status-badge-inactivo';

  return 'status-badge';
}

function CajaChicaPage() {
  const admin = esAdministrador();
  const [cajas, setCajas] = useState([]);
  const [cajaId, setCajaId] = useState('');
  const [resumen, setResumen] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [arqueos, setArqueos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  const [todosFondos, setTodosFondos] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [fondoResponsablesId, setFondoResponsablesId] = useState('');
  const [formGasto, setFormGasto] = useState(FORM_GASTO_INICIAL);
  const [formArqueo, setFormArqueo] = useState(FORM_ARQUEO_INICIAL);
  const [formFondo, setFormFondo] = useState(FORM_FONDO_INICIAL);
  const [formConcepto, setFormConcepto] = useState(FORM_CONCEPTO_INICIAL);
  const [formEntrada, setFormEntrada] = useState(FORM_ENTRADA_INICIAL);
  const [formResponsable, setFormResponsable] = useState(FORM_RESPONSABLE_INICIAL);
  const [vista, setVista] = useState('gastos');
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [evidencias, setEvidencias] = useState({ gasto: null, items: [] });
  const [modal, setModal] = useState({ abierto: false });

  const cajaSeleccionada = useMemo(() => {
    return cajas.find(item => String(item.CajaChicaId) === String(cajaId)) || null;
  }, [cajas, cajaId]);

  const monedas = useMemo(
    () => catalogos.filter(item => item.Tipo === 'Moneda' && item.Activo),
    [catalogos]
  );
  const puedeMostrarPanel = admin || Boolean(cajaSeleccionada);

  const saldoSistemaArqueo = Number(resumen?.saldoActual || cajaSeleccionada?.SaldoActual || 0);
  const diferenciaArqueo = formArqueo.saldoFisico === ''
    ? null
    : Number((Number(formArqueo.saldoFisico || 0) - saldoSistemaArqueo).toFixed(2));

  useEffect(() => {
    cargarInicial();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cajaId) cargarDatosCaja(cajaId);
  }, [cajaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (admin && vista === 'responsables' && fondoResponsablesId) cargarResponsables(fondoResponsablesId);
  }, [admin, vista, fondoResponsablesId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function cargarInicial() {
    setLoading(true);

    try {
      const [cajasResp, conceptosResp, fondosResp, usuariosResp, catalogosResp] = await Promise.all([
        obtenerMisCajasChicas(),
        listarConceptosCajaChica({ activo: '' }),
        admin ? listarFondosCajaChica() : Promise.resolve({ fondos: [] }),
        admin ? listarUsuariosDisponiblesCajaChica() : Promise.resolve({ usuarios: [] }),
        admin ? api.get('/catalogos?incluirInactivos=1') : Promise.resolve({ data: [] })
      ]);

      const cajasData = cajasResp.fondos || cajasResp.cajas || [];
      const fondosData = fondosResp.fondos || [];
      const cajasVista = admin ? fondosData : cajasData;
      setCajas(cajasVista);
      setConceptos(conceptosResp.conceptos || []);
      setCatalogos(catalogosResp.data || []);
      setTodosFondos(fondosData);
      setUsuariosDisponibles(usuariosResp.usuarios || []);
      if (admin && fondosData.length > 0) setFondoResponsablesId(prev => prev || String(fondosData[0].CajaChicaId));
      if (admin && fondosData.length === 0) setVista('fondos');

      if (cajasVista.length === 1) {
        setCajaId(String(cajasVista[0].CajaChicaId));
      } else if (cajasVista.length > 1) {
        setCajaId(prev => prev || String(cajasVista[0].CajaChicaId));
      } else {
        setCajaId('');
      }
    } catch (error) {
      abrirMensaje('Caja Chica', mensajeError(error), 'danger');
    } finally {
      setLoading(false);
    }
  }

  async function cargarDatosCaja(id) {
    try {
      const [resumenResp, gastosResp, arqueosResp] = await Promise.all([
        obtenerResumenCajaChica(id),
        listarGastosCajaChica(id, { pageSize: 25 }),
        listarArqueosCajaChica(id, { pageSize: 10 })
      ]);

      setResumen(resumenResp);
      setGastos(gastosResp.gastos || []);
      setArqueos(arqueosResp.arqueos || []);
    } catch (error) {
      abrirMensaje('Caja Chica', mensajeError(error), 'danger');
    }
  }

  function abrirMensaje(titulo, mensaje, variante = 'info') {
    setModal({
      abierto: true,
      titulo,
      mensaje,
      variante,
      mostrarCancelar: false,
      textoAceptar: 'Aceptar',
      onAceptar: () => setModal({ abierto: false }),
      onCancelar: () => setModal({ abierto: false })
    });
  }

  async function guardarGasto(event) {
    event.preventDefault();

    if (!cajaId) return abrirMensaje('Caja Chica', 'Selecciona una caja chica.', 'warning');
    if (!formGasto.conceptoTexto.trim()) return abrirMensaje('Nuevo gasto', 'El concepto es obligatorio.', 'warning');

    setGuardando(true);

    try {
      const resp = await registrarGastoCajaChica(cajaId, {
        monto: Number(formGasto.monto),
        conceptoTexto: formGasto.conceptoTexto,
        conceptoId: formGasto.conceptoId || null,
        descripcion: formGasto.descripcion,
        referencia: formGasto.referencia
      });

      if (formGasto.archivo && resp.gasto?.Id) {
        await subirEvidenciaGasto(resp.gasto.Id, formGasto.archivo);
      }

      setFormGasto(FORM_GASTO_INICIAL);
      await cargarDatosCaja(cajaId);
      abrirMensaje('Gasto registrado', 'El gasto se guardó correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Nuevo gasto', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  async function guardarArqueo(event) {
    event.preventDefault();

    if (!cajaId) return abrirMensaje('Arqueo', 'Selecciona una caja chica.', 'warning');

    setGuardando(true);

    try {
      await registrarArqueoCajaChica(cajaId, {
        saldoFisico: Number(formArqueo.saldoFisico),
        observaciones: formArqueo.observaciones
      });

      setFormArqueo(FORM_ARQUEO_INICIAL);
      await cargarDatosCaja(cajaId);
      abrirMensaje('Arqueo registrado', 'El arqueo se guardó correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Arqueo', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  async function guardarEntrada(event) {
    event.preventDefault();
    if (!cajaId) return abrirMensaje('Fondeo', 'Selecciona un fondo de caja chica.', 'warning');
    setGuardando(true);

    try {
      const payload = {
        monto: Number(formEntrada.monto),
        concepto: formEntrada.concepto,
        referencia: formEntrada.referencia,
        folioRecibo: formEntrada.folioRecibo,
        reciboId: formEntrada.reciboId ? Number(formEntrada.reciboId) : null
      };
      if (formEntrada.tipo === 'REPOSICION') {
        await registrarReposicionCajaChica(cajaId, payload);
      } else {
        await registrarFondeoCajaChica(cajaId, payload);
      }
      setFormEntrada(FORM_ENTRADA_INICIAL);
      await cargarDatosCaja(cajaId);
      await cargarInicial();
      abrirMensaje('Movimiento registrado', 'El movimiento se guardó correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Fondeo', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  async function guardarFondo(event) {
    event.preventDefault();
    setGuardando(true);

    try {
      const payload = { ...formFondo, MonedaId: Number(formFondo.MonedaId), Activa: Boolean(formFondo.Activa) };
      if (formFondo.Id) {
        await editarFondoCajaChica(formFondo.Id, payload);
      } else {
        await crearFondoCajaChica(payload);
      }
      setFormFondo(FORM_FONDO_INICIAL);
      await cargarInicial();
      abrirMensaje('Fondo guardado', 'El fondo de caja chica se guardó correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Fondos', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  async function guardarConcepto(event) {
    event.preventDefault();
    setGuardando(true);

    try {
      if (formConcepto.Id) {
        await editarConceptoCajaChica(formConcepto.Id, formConcepto);
      } else {
        await crearConceptoCajaChica(formConcepto);
      }
      setFormConcepto(FORM_CONCEPTO_INICIAL);
      await cargarInicial();
      abrirMensaje('Concepto guardado', 'El concepto se guardó correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Conceptos', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  function editarFondo(fondo) {
    setFormFondo({
      Id: fondo.CajaChicaId,
      Nombre: fondo.Nombre || '',
      Descripcion: fondo.Descripcion || '',
      MonedaId: fondo.MonedaId ? String(fondo.MonedaId) : '',
      MontoFondoAutorizado: fondo.MontoFondoAutorizado ?? '',
      Activa: Boolean(fondo.Activa)
    });
    setVista('fondos');
  }

  async function cambiarEstatusFondo(fondo, activa) {
    setGuardando(true);
    try {
      if (activa) await activarFondoCajaChica(fondo.CajaChicaId);
      else await desactivarFondoCajaChica(fondo.CajaChicaId);
      await cargarInicial();
      abrirMensaje('Fondos', activa ? 'Fondo activado correctamente.' : 'Fondo desactivado correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Fondos', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  function editarConcepto(concepto) {
    setFormConcepto({
      Id: concepto.Id,
      Nombre: concepto.Nombre || '',
      Descripcion: concepto.Descripcion || '',
      Activo: Boolean(concepto.Activo)
    });
    setVista('conceptos');
  }

  async function cambiarEstatusConcepto(concepto, activo) {
    setGuardando(true);
    try {
      if (activo) await activarConceptoCajaChica(concepto.Id);
      else await desactivarConceptoCajaChica(concepto.Id);
      await cargarInicial();
      abrirMensaje('Conceptos', activo ? 'Concepto activado correctamente.' : 'Concepto desactivado correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Conceptos', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }
  async function cargarResponsables(fondoId) {
    try {
      const resp = await listarResponsablesFondoCajaChica(fondoId);
      setResponsables(resp.responsables || []);
    } catch (error) {
      abrirMensaje('Responsables', mensajeError(error), 'danger');
    }
  }

  async function guardarResponsable(event) {
    event.preventDefault();
    if (!fondoResponsablesId) return abrirMensaje('Responsables', 'Selecciona un fondo.', 'warning');
    if (!formResponsable.UsuarioId) return abrirMensaje('Responsables', 'Selecciona un usuario.', 'warning');

    setGuardando(true);
    try {
      await asignarResponsableFondoCajaChica(fondoResponsablesId, {
        ...formResponsable,
        UsuarioId: Number(formResponsable.UsuarioId)
      });
      setFormResponsable(FORM_RESPONSABLE_INICIAL);
      await cargarResponsables(fondoResponsablesId);
      await cargarInicial();
      abrirMensaje('Responsable asignado', 'La asignación se guardó correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Responsables', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  function cambiarPermisoResponsable(id, campo, valor) {
    setResponsables(prev => prev.map(item => item.Id === id ? { ...item, [campo]: valor } : item));
  }

  async function guardarPermisosResponsable(responsable) {
    setGuardando(true);
    try {
      await actualizarResponsableCajaChica(responsable.Id, {
        PuedeRegistrarGastos: Boolean(responsable.PuedeRegistrarGastos),
        PuedeSubirEvidencia: Boolean(responsable.PuedeSubirEvidencia),
        PuedeVerSaldo: Boolean(responsable.PuedeVerSaldo),
        Activo: Boolean(responsable.Activo)
      });
      await cargarResponsables(fondoResponsablesId);
      abrirMensaje('Permisos actualizados', 'Los permisos del responsable se guardaron correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Responsables', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  async function cambiarEstatusResponsable(responsable, activo) {
    setGuardando(true);
    try {
      if (activo) {
        await activarResponsableCajaChica(responsable.Id);
      } else {
        await desactivarResponsableCajaChica(responsable.Id);
      }
      await cargarResponsables(fondoResponsablesId);
      abrirMensaje('Responsables', activo ? 'Responsable activado correctamente.' : 'Responsable desactivado correctamente.', 'success');
    } catch (error) {
      abrirMensaje('Responsables', mensajeError(error), 'danger');
    } finally {
      setGuardando(false);
    }
  }

  async function cargarEvidencias(gasto) {
    try {
      const resp = await listarEvidenciasGasto(gasto.Id);
      setEvidencias({ gasto, items: resp.evidencias || [] });
      setVista('evidencias');
    } catch (error) {
      abrirMensaje('Evidencias', mensajeError(error), 'danger');
    }
  }

  async function abrirEvidencia(evidencia) {
    try {
      const response = await descargarEvidenciaGasto(evidencias.gasto.Id, evidencia.Id);
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      abrirMensaje('Evidencia', mensajeError(error), 'danger');
    }
  }

  function solicitarCancelarGasto(gasto) {
    setModal({
      abierto: true,
      titulo: 'Cancelar gasto',
      mensaje: `Gasto #${gasto.Id}: ${gasto.ConceptoTexto}`,
      variante: 'danger',
      mostrarInput: true,
      inputLabel: 'Motivo de cancelación',
      textoAceptar: 'Cancelar gasto',
      onCancelar: () => setModal({ abierto: false }),
      onAceptar: async (motivo) => {
        setGuardando(true);

        try {
          await cancelarGastoCajaChica(gasto.Id, { motivo });
          setModal({ abierto: false });
          await cargarDatosCaja(cajaId);
        } catch (error) {
          abrirMensaje('Cancelar gasto', mensajeError(error), 'danger');
        } finally {
          setGuardando(false);
        }
      }
    });
  }

  const moneda = resumen?.fondo?.Moneda || cajaSeleccionada?.Moneda;

  return (
    <div className="caja-chica-page">
      <div className="caja-chica-shell">
        <div className="caja-chica-header">
          <div>
            <h1>Caja Chica</h1>
            <p>{admin ? 'Administración de cajas chicas, gastos y arqueos.' : 'Registro móvil de gastos y arqueos.'}</p>
          </div>

          <select className="form-select" value={cajaId} onChange={event => setCajaId(event.target.value)} disabled={loading || cajas.length <= 1}>
            <option value="">Seleccione caja</option>
            {cajas.map(caja => (
              <option key={caja.CajaChicaId} value={caja.CajaChicaId}>{caja.Nombre} - {caja.Moneda}</option>
            ))}
          </select>
        </div>

        {loading && <div className="alert alert-light border">Cargando caja chica...</div>}
        {!loading && !admin && cajas.length === 0 && <div className="alert alert-warning">No tienes cajas chicas asignadas.</div>}

        {!loading && puedeMostrarPanel && (
          <>
            {cajaSeleccionada && <section className="caja-chica-balance">
              <div>
                <span>Caja</span>
                <strong>{cajaSeleccionada.Nombre}</strong>
              </div>
              <div>
                <span>Saldo actual</span>
                <strong className="money-value d-block">{formatoImporte(resumen?.saldoActual ?? cajaSeleccionada.SaldoActual, moneda)}</strong>
              </div>
              <div>
                <span>Gastos del día</span>
                <strong className="money-value d-block">{formatoImporte(resumen?.gastosDelDia, moneda)}</strong>
              </div>
              <div>
                <span>Gastos del mes</span>
                <strong className="money-value d-block">{formatoImporte(resumen?.gastosDelMes, moneda)}</strong>
              </div>
            </section>}

            <div className="caja-chica-tabs">
              {[
                ['gastos', 'Gastos'],
                ['nuevo', 'Nuevo gasto'],
                ['arqueo', 'Arqueo'],
                ['arqueos', 'Historial'],
                ...(admin ? [['entrada', 'Fondeo'], ['fondos', 'Fondos'], ['conceptos', 'Conceptos'], ['responsables', 'Responsables']] : [])
              ].map(([key, label]) => (
                <button key={key} className={vista === key ? 'active' : ''} type="button" onClick={() => setVista(key)}>{label}</button>
              ))}
            </div>

            {vista === 'nuevo' && (
              <form className="caja-chica-panel" onSubmit={guardarGasto}>
                <h2>Nuevo gasto</h2>
                <label className="form-label">Monto</label>
                <input className="form-control money-input" type="number" step="0.01" min="0.01" value={formGasto.monto} onChange={event => setFormGasto(prev => ({ ...prev, monto: event.target.value }))} required />

                <label className="form-label mt-2">Concepto</label>
                {conceptos.length > 0 && (
                  <select className="form-select mb-2" value={formGasto.conceptoId} onChange={event => {
                    const concepto = conceptos.find(item => String(item.Id) === event.target.value);
                    setFormGasto(prev => ({ ...prev, conceptoId: event.target.value, conceptoTexto: concepto?.Nombre || prev.conceptoTexto }));
                  }}>
                    <option value="">Concepto libre</option>
                    {conceptos.map(item => <option key={item.Id} value={item.Id}>{item.Nombre}</option>)}
                  </select>
                )}
                <input className="form-control" value={formGasto.conceptoTexto} onChange={event => setFormGasto(prev => ({ ...prev, conceptoTexto: event.target.value }))} placeholder="Agua, copias, estacionamiento..." maxLength="180" required />

                <label className="form-label mt-2">Descripción</label>
                <textarea className="form-control" rows="3" value={formGasto.descripcion} onChange={event => setFormGasto(prev => ({ ...prev, descripcion: event.target.value }))} maxLength="600" />

                <label className="form-label mt-2">Referencia</label>
                <input className="form-control" value={formGasto.referencia} onChange={event => setFormGasto(prev => ({ ...prev, referencia: event.target.value }))} maxLength="160" />

                <label className="form-label mt-2">Evidencia</label>
                <input className="form-control" type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" onChange={event => setFormGasto(prev => ({ ...prev, archivo: event.target.files?.[0] || null }))} />

                <button className="btn btn-primary w-100 mt-3" type="submit" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar gasto'}</button>
              </form>
            )}

            {vista === 'gastos' && (
              <section className="caja-chica-panel">
                <h2>Últimos gastos</h2>
                <div className="caja-chica-list">
                  {gastos.map(gasto => (
                    <article className="caja-chica-item" key={gasto.Id}>
                      <div>
                        <strong>{gasto.ConceptoTexto}</strong>
                        <span>{formatoFecha(gasto.FechaGasto)} · {gasto.Referencia || 'Sin referencia'}</span>
                      </div>
                      <div className="caja-chica-item-actions">
                        <strong className="money-value">{formatoImporte(gasto.Monto, moneda)}</strong>
                        <span className={obtenerClaseEstatusCajaChica(gasto.Estatus || (gasto.Cancelado ? 'CANCELADO' : 'REGISTRADO'))}>{gasto.Estatus || (gasto.Cancelado ? 'CANCELADO' : 'REGISTRADO')}</span>
                        <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => cargarEvidencias(gasto)}>Evidencias ({gasto.TotalEvidencias || 0})</button>
                        {admin && !gasto.Cancelado && <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => solicitarCancelarGasto(gasto)}>Cancelar</button>}
                      </div>
                    </article>
                  ))}
                  {gastos.length === 0 && <div className="text-muted">Sin gastos registrados.</div>}
                </div>
              </section>
            )}

            {vista === 'evidencias' && (
              <section className="caja-chica-panel">
                <div className="d-flex justify-content-between gap-2 align-items-center mb-2">
                  <h2 className="mb-0">Evidencias</h2>
                  <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setVista('gastos')}>Volver</button>
                </div>
                <p className="text-muted small mb-3">Gasto #{evidencias.gasto?.Id}: {evidencias.gasto?.ConceptoTexto}</p>
                {evidencias.items.map(item => (
                  <button key={item.Id} className="btn btn-outline-primary w-100 mb-2" type="button" onClick={() => abrirEvidencia(item)}>
                    {item.NombreOriginal || `Evidencia ${item.Id}`}
                  </button>
                ))}
                {evidencias.items.length === 0 && <div className="text-muted">Este gasto no tiene evidencias.</div>}
              </section>
            )}

            {vista === 'arqueo' && (
              <form className="caja-chica-panel" onSubmit={guardarArqueo}>
                <h2>Arqueo físico</h2>
                <div className="caja-chica-arqueo-row"><span>Saldo sistema</span><strong className="money-value">{formatoImporte(saldoSistemaArqueo, moneda)}</strong></div>
                <label className="form-label mt-2">Saldo físico</label>
                <input className="form-control money-input" type="number" step="0.01" min="0" value={formArqueo.saldoFisico} onChange={event => setFormArqueo(prev => ({ ...prev, saldoFisico: event.target.value }))} required />
                <div className="caja-chica-arqueo-row mt-2"><span>Diferencia</span><strong className="money-value">{diferenciaArqueo === null ? '-' : formatoImporte(diferenciaArqueo, moneda)}</strong></div>
                <label className="form-label mt-2">Observaciones</label>
                <textarea className="form-control" rows="3" value={formArqueo.observaciones} onChange={event => setFormArqueo(prev => ({ ...prev, observaciones: event.target.value }))} maxLength="600" />
                <button className="btn btn-primary w-100 mt-3" type="submit" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar arqueo'}</button>
              </form>
            )}

            {vista === 'arqueos' && (
              <section className="caja-chica-panel">
                <h2>Arqueos</h2>
                <div className="table-wrap">
                  <table className="table table-sm table-hover align-middle erp-table-striped">
                    <thead><tr><th>Fecha</th><th className="money-header">Sistema</th><th className="money-header">Físico</th><th className="money-header">Diferencia</th><th>Estatus</th></tr></thead>
                    <tbody>
                      {arqueos.map(item => (
                        <tr key={item.Id}>
                          <td>{formatoFecha(item.FechaArqueo)}</td>
                          <td className="money-cell">{formatoImporte(item.SaldoSistema, moneda)}</td>
                          <td className="money-cell">{formatoImporte(item.SaldoFisico, moneda)}</td>
                          <td className="money-cell">{formatoImporte(item.Diferencia, moneda)}</td>
                          <td><span className={obtenerClaseEstatusCajaChica(item.Estatus)}>{item.Estatus || '-'}</span></td>
                        </tr>
                      ))}
                      {arqueos.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-3">Sin arqueos.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {admin && vista === 'entrada' && (
              <form className="caja-chica-panel" onSubmit={guardarEntrada}>
                <h2>Fondeo / Reposición</h2>
                <label className="form-label">Tipo</label>
                <select className="form-select" value={formEntrada.tipo} onChange={event => setFormEntrada(prev => ({ ...prev, tipo: event.target.value }))}>
                  <option value="FONDEO">Fondeo</option>
                  <option value="REPOSICION">Reposición</option>
                </select>
                <label className="form-label mt-2">Monto</label>
                <input className="form-control money-input" type="number" step="0.01" min="0.01" value={formEntrada.monto} onChange={event => setFormEntrada(prev => ({ ...prev, monto: event.target.value }))} required />
                <label className="form-label mt-2">Concepto</label>
                <input className="form-control" value={formEntrada.concepto} onChange={event => setFormEntrada(prev => ({ ...prev, concepto: event.target.value }))} required maxLength="180" />
                <label className="form-label mt-2">Referencia</label>
                <input className="form-control" value={formEntrada.referencia} onChange={event => setFormEntrada(prev => ({ ...prev, referencia: event.target.value }))} maxLength="160" />
                <label className="form-label mt-2">Folio recibo</label>
                <input className="form-control" value={formEntrada.folioRecibo} onChange={event => setFormEntrada(prev => ({ ...prev, folioRecibo: event.target.value }))} maxLength="60" />
                <label className="form-label mt-2">Recibo ID</label>
                <input className="form-control" type="number" value={formEntrada.reciboId} onChange={event => setFormEntrada(prev => ({ ...prev, reciboId: event.target.value }))} />
                <button className="btn btn-primary w-100 mt-3" type="submit" disabled={guardando}>{guardando ? 'Guardando...' : 'Registrar movimiento'}</button>
              </form>
            )}

            {admin && vista === 'fondos' && (
              <section className="caja-chica-panel">
                <h2>Fondos</h2>
                <form onSubmit={guardarFondo} className="row g-2 mb-3 align-items-end">
                  <div className="col-md-3"><label className="form-label">Nombre</label><input className="form-control" value={formFondo.Nombre} onChange={event => setFormFondo(prev => ({ ...prev, Nombre: event.target.value }))} required /></div>
                  <div className="col-md-3"><label className="form-label">Descripción</label><input className="form-control" value={formFondo.Descripcion} onChange={event => setFormFondo(prev => ({ ...prev, Descripcion: event.target.value }))} /></div>
                  <div className="col-md-2"><label className="form-label">Moneda</label><select className="form-select" value={formFondo.MonedaId} onChange={event => setFormFondo(prev => ({ ...prev, MonedaId: event.target.value }))} required><option value="">Seleccione</option>{monedas.map(item => <option key={item.Id} value={item.Id}>{item.Valor}</option>)}</select></div>
                  <div className="col-md-2"><label className="form-label">Monto autorizado</label><input className="form-control money-input" type="number" step="0.01" min="0" value={formFondo.MontoFondoAutorizado} onChange={event => setFormFondo(prev => ({ ...prev, MontoFondoAutorizado: event.target.value }))} /></div>
                  <div className="col-md-2"><label className="form-check mb-2"><input className="form-check-input" type="checkbox" checked={formFondo.Activa} onChange={event => setFormFondo(prev => ({ ...prev, Activa: event.target.checked }))} /> <span className="form-check-label">Activa</span></label><div className="d-flex gap-2"><button className="btn btn-primary flex-fill" type="submit" disabled={guardando}>{formFondo.Id ? 'Guardar' : 'Crear'}</button>{formFondo.Id && <button className="btn btn-outline-secondary" type="button" onClick={() => setFormFondo(FORM_FONDO_INICIAL)}>Limpiar</button>}</div></div>
                </form>
                <div className="table-wrap"><table className="table table-sm table-hover erp-table-striped"><thead><tr><th>Nombre</th><th>Moneda</th><th className="money-header">Monto autorizado</th><th className="money-header">Saldo actual</th><th>Activa</th><th>Acciones</th></tr></thead><tbody>{todosFondos.map(fondo => <tr key={fondo.CajaChicaId}><td>{fondo.Nombre}</td><td>{fondo.Moneda}</td><td className="money-cell">{fondo.MontoFondoAutorizado === null || fondo.MontoFondoAutorizado === undefined ? '-' : formatoImporte(fondo.MontoFondoAutorizado, fondo.Moneda)}</td><td className="money-cell">{formatoImporte(fondo.SaldoActual, fondo.Moneda)}</td><td><span className={obtenerClaseEstatusCajaChica(fondo.Activa ? 'ACTIVO' : 'INACTIVO')}>{fondo.Activa ? 'Sí' : 'No'}</span></td><td><div className="d-flex flex-wrap gap-2"><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => editarFondo(fondo)}>Editar</button>{fondo.Activa ? <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => cambiarEstatusFondo(fondo, false)} disabled={guardando}>Desactivar</button> : <button className="btn btn-sm btn-outline-success" type="button" onClick={() => cambiarEstatusFondo(fondo, true)} disabled={guardando}>Activar</button>}</div></td></tr>)}{todosFondos.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-3">Sin fondos. Cree el primer fondo de caja chica.</td></tr>}</tbody></table></div>
              </section>
            )}

            {admin && vista === 'conceptos' && (
              <section className="caja-chica-panel">
                <h2>Conceptos</h2>
                <form onSubmit={guardarConcepto} className="row g-2 mb-3 align-items-end">
                  <div className="col-md-4"><label className="form-label">Nombre</label><input className="form-control" value={formConcepto.Nombre} onChange={event => setFormConcepto(prev => ({ ...prev, Nombre: event.target.value }))} required /></div>
                  <div className="col-md-4"><label className="form-label">Descripción</label><input className="form-control" value={formConcepto.Descripcion} onChange={event => setFormConcepto(prev => ({ ...prev, Descripcion: event.target.value }))} /></div>
                  <div className="col-md-2"><label className="form-check mb-2"><input className="form-check-input" type="checkbox" checked={formConcepto.Activo} onChange={event => setFormConcepto(prev => ({ ...prev, Activo: event.target.checked }))} /> <span className="form-check-label">Activo</span></label></div>
                  <div className="col-md-2"><div className="d-flex gap-2"><button className="btn btn-primary flex-fill" type="submit" disabled={guardando}>{formConcepto.Id ? 'Guardar' : 'Crear'}</button>{formConcepto.Id && <button className="btn btn-outline-secondary" type="button" onClick={() => setFormConcepto(FORM_CONCEPTO_INICIAL)}>Limpiar</button>}</div></div>
                </form>
                <div className="table-wrap"><table className="table table-sm table-hover erp-table-striped"><thead><tr><th>Nombre</th><th>Descripción</th><th>Activo</th><th>Acciones</th></tr></thead><tbody>{conceptos.map(concepto => <tr key={concepto.Id}><td>{concepto.Nombre}</td><td>{concepto.Descripcion || '-'}</td><td><span className={obtenerClaseEstatusCajaChica(concepto.Activo ? 'ACTIVO' : 'INACTIVO')}>{concepto.Activo ? 'Sí' : 'No'}</span></td><td><div className="d-flex flex-wrap gap-2"><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => editarConcepto(concepto)}>Editar</button>{concepto.Activo ? <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => cambiarEstatusConcepto(concepto, false)} disabled={guardando}>Desactivar</button> : <button className="btn btn-sm btn-outline-success" type="button" onClick={() => cambiarEstatusConcepto(concepto, true)} disabled={guardando}>Activar</button>}</div></td></tr>)}{conceptos.length === 0 && <tr><td colSpan="4" className="text-center text-muted py-3">Sin conceptos registrados.</td></tr>}</tbody></table></div>
              </section>
            )}
            {admin && vista === 'responsables' && (
              <section className="caja-chica-panel">
                <h2>Responsables</h2>
                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Fondo</label>
                    <select className="form-select" value={fondoResponsablesId} onChange={event => setFondoResponsablesId(event.target.value)}>
                      <option value="">Seleccione fondo</option>
                      {todosFondos.map(fondo => (
                        <option key={fondo.CajaChicaId} value={fondo.CajaChicaId}>{fondo.Nombre} - {fondo.Moneda}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {todosFondos.length === 0 && <div className="alert alert-warning">Primero cree un fondo de caja chica.</div>}

                <form onSubmit={guardarResponsable} className="row g-2 align-items-end mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Usuario</label>
                    <select className="form-select" value={formResponsable.UsuarioId} onChange={event => setFormResponsable(prev => ({ ...prev, UsuarioId: event.target.value }))} required>
                      <option value="">Seleccione usuario</option>
                      {usuariosDisponibles.map(usuario => (
                        <option key={usuario.Id} value={usuario.Id}>{usuario.Nombre} ({usuario.Usuario})</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-5">
                    <div className="d-flex flex-wrap gap-3">
                      <label className="form-check mb-0">
                        <input className="form-check-input" type="checkbox" checked={formResponsable.PuedeRegistrarGastos} onChange={event => setFormResponsable(prev => ({ ...prev, PuedeRegistrarGastos: event.target.checked }))} />
                        <span className="form-check-label">Registrar gastos</span>
                      </label>
                      <label className="form-check mb-0">
                        <input className="form-check-input" type="checkbox" checked={formResponsable.PuedeSubirEvidencia} onChange={event => setFormResponsable(prev => ({ ...prev, PuedeSubirEvidencia: event.target.checked }))} />
                        <span className="form-check-label">Subir evidencia</span>
                      </label>
                      <label className="form-check mb-0">
                        <input className="form-check-input" type="checkbox" checked={formResponsable.PuedeVerSaldo} onChange={event => setFormResponsable(prev => ({ ...prev, PuedeVerSaldo: event.target.checked }))} />
                        <span className="form-check-label">Ver saldo</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-md-3"><button className="btn btn-primary w-100" type="submit" disabled={guardando || !fondoResponsablesId || todosFondos.length === 0}>Asignar</button></div>
                </form>

                <div className="table-wrap">
                  <table className="table table-sm table-hover align-middle erp-table-striped">
                    <thead><tr><th>Usuario</th><th>Nombre</th><th>Rol</th><th>Gastos</th><th>Evidencia</th><th>Saldo</th><th>Activo</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {responsables.map(responsable => (
                        <tr key={responsable.Id}>
                          <td>{responsable.Usuario}</td>
                          <td>{responsable.Nombre}</td>
                          <td>{responsable.Rol}</td>
                          <td><input className="form-check-input" type="checkbox" checked={Boolean(responsable.PuedeRegistrarGastos)} onChange={event => cambiarPermisoResponsable(responsable.Id, 'PuedeRegistrarGastos', event.target.checked)} /></td>
                          <td><input className="form-check-input" type="checkbox" checked={Boolean(responsable.PuedeSubirEvidencia)} onChange={event => cambiarPermisoResponsable(responsable.Id, 'PuedeSubirEvidencia', event.target.checked)} /></td>
                          <td><input className="form-check-input" type="checkbox" checked={Boolean(responsable.PuedeVerSaldo)} onChange={event => cambiarPermisoResponsable(responsable.Id, 'PuedeVerSaldo', event.target.checked)} /></td>
                          <td><span className={obtenerClaseEstatusCajaChica(responsable.Activo ? 'ACTIVO' : 'INACTIVO')}>{responsable.Activo ? 'Sí' : 'No'}</span></td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => guardarPermisosResponsable(responsable)} disabled={guardando}>Guardar</button>
                              {responsable.Activo
                                ? <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => cambiarEstatusResponsable(responsable, false)} disabled={guardando}>Desactivar</button>
                                : <button className="btn btn-sm btn-outline-success" type="button" onClick={() => cambiarEstatusResponsable(responsable, true)} disabled={guardando}>Activar</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {responsables.length === 0 && <tr><td colSpan="8" className="text-center text-muted py-3">Sin responsables asignados.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <AppModal {...modal} aceptando={guardando} />
    </div>
  );
}

export default CajaChicaPage;










